import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Star, Clock, MapPin } from 'lucide-react';
import { toursAPI } from '../../lib/api';
import { useLanguage } from '../../contexts/LanguageContext';

export const FeaturedToursCarousel = () => {
  const [tours, setTours] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchFeaturedTours = async () => {
      try {
        const response = await toursAPI.getFeatured();
        setTours(response.data || []);
      } catch (error) {
        console.error('Error fetching featured tours:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedTours();
  }, []);

  // Get current pair of tours to display
  const getVisibleTours = () => {
    if (tours.length === 0) return [];
    if (tours.length === 1) return [tours[0]];
    
    const first = tours[currentIndex % tours.length];
    const second = tours[(currentIndex + 1) % tours.length];
    return [first, second];
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 2) % Math.max(tours.length, 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 2 + tours.length) % Math.max(tours.length, 1));
  };

  // Auto-advance slides
  useEffect(() => {
    if (tours.length <= 2) return;
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [tours.length]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-4">
        <div className="h-[200px] bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl animate-pulse" />
        <div className="h-[200px] bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (tours.length === 0) {
    return null;
  }

  const visibleTours = getVisibleTours();

  return (
    <div className="relative" data-testid="featured-carousel">
      {/* Tour Cards Grid - Side by Side */}
      <div className="grid grid-cols-2 gap-4">
        {visibleTours.map((tour, index) => (
          <FeaturedTourCard key={`${tour.id}-${index}`} tour={tour} t={t} />
        ))}
      </div>

      {/* Navigation Arrows - Only show if more than 2 tours */}
      {tours.length > 2 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={prevSlide}
            className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          {Array.from({ length: Math.ceil(tours.length / 2) }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index * 2)}
              className={`w-2 h-2 rounded-full transition-all ${
                Math.floor(currentIndex / 2) === index
                  ? 'bg-primary w-6'
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
          <button
            onClick={nextSlide}
            className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
            data-testid="carousel-next"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// Individual Featured Tour Card - Compact Version
const FeaturedTourCard = ({ tour, t }) => {
  // Badge color mapping based on selected color
  const getBadgeStyle = (color) => {
    const styles = {
      'white': 'bg-white text-gray-800 border border-gray-300',
      'red': 'bg-red-500 text-white',
      'green': 'bg-green-500 text-white',
      'yellow': 'bg-yellow-400 text-gray-900',
    };
    return styles[color] || 'bg-green-500 text-white';
  };

  const badgeText = tour.featured_badge || 'Featured';
  const badgeColor = tour.featured_badge_color || 'green';
  const badgeStyle = getBadgeStyle(badgeColor);

  return (
    <Link 
      to={`/tours/${tour.id}`}
      className="group relative overflow-hidden rounded-xl block"
      data-testid={`featured-tour-${tour.id}`}
    >
      {/* Background Image */}
      <div className="relative h-[200px]">
        <img
          src={tour.image_url}
          alt={tour.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        
        {/* Featured Badge - Customizable color and text */}
        <span className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold shadow-lg ${badgeStyle}`}>
          {badgeText}
        </span>

        {/* Discount Badge */}
        {tour.discount_percentage > 0 && (
          <span className="absolute top-3 right-3 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full shadow-lg">
            {tour.discount_percentage}% OFF
          </span>
        )}
        
        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {/* Title */}
          <h3 className="text-lg font-heading font-bold text-white mb-1 group-hover:text-secondary transition-colors line-clamp-1">
            {tour.title}
          </h3>
          
          {/* Meta Info */}
          <div className="flex items-center gap-2 text-white/80 text-xs mb-2">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {tour.duration}
            </span>
            <span className="flex items-center gap-1">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              {tour.rating || 4.8}
            </span>
          </div>
          
          {/* Price & CTA */}
          <div className="flex items-center justify-between">
            <div>
              {tour.discount_percentage > 0 ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-lg font-bold text-white">
                    ${(tour.price * (1 - tour.discount_percentage / 100)).toFixed(0)}
                  </span>
                  <span className="text-sm text-white/60 line-through">
                    ${tour.price}
                  </span>
                </div>
              ) : (
                <span className="text-lg font-bold text-white">
                  ${tour.price}
                </span>
              )}
            </div>
            
            <span className="px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-full group-hover:bg-primary/90 transition-colors">
              {t('bookNow')}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default FeaturedToursCarousel;
